import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { addToast, handleAxiosError, useModals } from 'core/notifications';
import { Form } from 'core/forms';
import i18n from 'i18next';
import InfoIcon from '@mui/icons-material/Info';

import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';

import axios from '@/util/axios';

import useConfirmAction from '../hooks/useConfirmAction';

import NewItemFormButtons from './newItemFormButtons';

import styles from '../styles/bulkEditForm.module.scss';

const BulkEditTooltip = (tooltip) => (
  <Tooltip content={tooltip}>
    <InfoIcon sx={{ height: 18, width: 18 }} />
  </Tooltip>
);

const BulkEditForm = ({
  body = '',
  confirmBody = '',
  getAxiosConfig = () => {},
  isConfirmationRequired = false,
  model = {},
  schema = {},
  submitText = i18n.t('update'),
  subtitle = '',
}) => {
  const { removeModal } = useModals();
  const [bulkEditModel, setBulkEditModel] = useState({});
  const [formState, setFormState] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getConfirmation = useConfirmAction({
    body: confirmBody,
    title: i18n.t('confirm-bulk-action'),
  });

  const bulkEditSchema = Object.entries(schema).reduce(
    (a, [key, { bulkEditTooltip, conditions, validators, ...field }]) => ({
      ...a,
      [key]: {
        ...field,
        ...(bulkEditTooltip && { LabelAddOn: () => BulkEditTooltip(bulkEditTooltip) }),
        fieldType: field.type,
        isClearable: !validators?.some((validator) => validator.type === 'isRequired'),
        type: 'BulkEdit',
      },
    }),
    {},
  );

  const handleDataErrors = ({ response }) => {
    const errors = response.data?.errors;

    errors?.forEach(({ message, source }) => {
      const field = formState[source.split('data.').pop()];

      if (field) {
        Object.assign(field, { error: message, hasError: true, isDirty: true });
      }
    });

    addToast(i18n.t('form-errors'), 'warning');
  };

  const handleLoad = (state) => {
    setFormState(state.formState);
  };

  const handleSubmit = async () => {
    if (isConfirmationRequired && !(await getConfirmation())) return;

    const data = {
      ...model,
      ...Object.fromEntries(Object.entries(bulkEditModel).map(([key, { value }]) => [key, value])),
    };

    setIsSubmitting(true);

    try {
      return removeModal({
        payload: (await axios(getAxiosConfig(data))).data?.data,
        type: 'submit',
      });
    } catch (error) {
      (error.response?.status === 422 ? handleDataErrors : handleAxiosError)(error);
    }

    setIsSubmitting(false);
  };

  const handleUpdate = (state) => {
    setFormState(state.formState);
    setBulkEditModel((prevModel) => ({ ...prevModel, ...state.update }));
  };

  const isSubmitDisabled = Object.values(bulkEditModel).every(
    (field) => field.condition === 'keep',
  );

  return (
    <div className={styles.element}>
      <div className={styles.header}>
        <Typo className={styles.title} element="h3" variant="d03">
          {i18n.t('bulk-edit')}
          {subtitle && (
            <>
              <span className={styles.divider} />
              <Typo element="span" variant="d02">
                {subtitle}
              </Typo>
            </>
          )}
        </Typo>
        <Typo color="secondary">{body}</Typo>
      </div>
      <div className={styles.form}>
        <Form
          model={bulkEditModel}
          onLoad={handleLoad}
          onUpdate={handleUpdate}
          schema={bulkEditSchema}
        />
      </div>
      <NewItemFormButtons
        isFullScreen={false}
        isSubmitDisabled={isSubmitDisabled}
        isSubmitting={isSubmitting}
        onCancel={removeModal}
        onSubmit={handleSubmit}
        submitText={submitText}
      />
    </div>
  );
};

BulkEditForm.propTypes = {
  body: PropTypes.string,
  confirmBody: PropTypes.string,
  getAxiosConfig: PropTypes.func,
  isConfirmationRequired: PropTypes.bool,
  model: PropTypes.shape({}),
  schema: PropTypes.shape({}),
  submitText: PropTypes.string,
  subtitle: PropTypes.string,
};

export default BulkEditForm;
